/**
 * Admin Dashboard
 *
 * Platform admin view: scans, maintenance tasks, and KB entries.
 * Restricted to users with isPlatformAdmin = true.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { RefreshCw, BarChart3, Wrench, BookOpen } from 'lucide-react';
import { Navbar } from '../../../components/Navbar';
import Footer from '../../../components/Footer';
import api from '../../../services/authenticatedApi';
import { logger } from '../../../utils/logger';
import type { Scan, Task, MtEntry, Pagination, Tab } from './types';
import { PAGE_SIZE, MT_PAGE_SIZE } from './types';
import { ScansTab } from './ScansTab';
import { TasksTab } from './TasksTab';
import { MaintenanceTab } from './MaintenanceTab';

export const AdminScansPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const TAB_ROUTES: Record<Tab, string> = {
    scans: '/admin/scans',
    tasks: '/admin/task-executions',
    mt: '/admin/maintenance-tasks',
  };
  const ROUTE_TABS: Record<string, Tab> = {
    '/admin/scans': 'scans',
    '/admin/task-executions': 'tasks',
    '/admin/maintenance-tasks': 'mt',
  };
  const activeTab: Tab = ROUTE_TABS[location.pathname] ?? 'scans';
  const setActiveTab = (tab: Tab) => navigate(TAB_ROUTES[tab]);

  // Scans state
  const [scans, setScans] = useState<Scan[]>([]);
  const [scanPagination, setScanPagination] = useState<Pagination | null>(null);
  const [scansLoading, setScansLoading] = useState(true);
  const [scansError, setScansError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [scanPage, setScanPage] = useState(0);

  // Tasks state
  const [tasks, setTasks] = useState<Task[]>([]);
  const [taskPagination, setTaskPagination] = useState<Pagination | null>(null);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [tasksLoaded, setTasksLoaded] = useState(false);
  const [taskPage, setTaskPage] = useState(0);

  // KB state
  const [mtEntries, setKbEntries] = useState<MtEntry[]>([]);
  const [mtLoading, setKbLoading] = useState(false);
  const [mtLoaded, setKbLoaded] = useState(false);
  const [mtSearch, setKbSearch] = useState('');
  const [mtPage, setMtPage] = useState(0);

  // ── Fetchers ────────────────────────────────────────────────────────────────

  const fetchScans = useCallback(async () => {
    setScansLoading(true);
    setScansError(null);
    try {
      const params = new URLSearchParams({
        limit: PAGE_SIZE.toString(),
        offset: (scanPage * PAGE_SIZE).toString(),
        sortOrder: 'desc',
      });
      if (search) params.append('search', search);
      const res = await api.get(`/admin/scans?${params}`);
      if (res.data.success) {
        setScans(res.data.data.scans);
        setScanPagination(res.data.data.pagination);
      }
    } catch (err: unknown) {
      const e = err as { response?: { status?: number } };
      setScansError(
        e.response?.status === 403
          ? 'Access denied. Platform admin privileges required.'
          : 'Failed to load scans.',
      );
      logger.error('Failed to fetch scans', { error: err });
    } finally {
      setScansLoading(false);
    }
  }, [scanPage, search]);

  const fetchTasks = useCallback(async () => {
    setTasksLoading(true);
    try {
      const params = new URLSearchParams({
        limit: PAGE_SIZE.toString(),
        offset: (taskPage * PAGE_SIZE).toString(),
      });
      const res = await api.get(`/admin/tasks?${params}`);
      if (res.data.success) {
        setTasks(res.data.data.tasks);
        setTaskPagination(res.data.data.pagination);
        setTasksLoaded(true);
      }
    } catch (err) {
      logger.error('Failed to fetch tasks', { error: err });
    } finally {
      setTasksLoading(false);
    }
  }, [taskPage]);

  const fetchMts = useCallback(async () => {
    setKbLoading(true);
    try {
      const res = await api.get('/admin/task-catalog');
      if (res.data.success) {
        setKbEntries(res.data.data.entries);
        setKbLoaded(true);
      }
    } catch (err) {
      logger.error('Failed to fetch KB entries', { error: err });
    } finally {
      setKbLoading(false);
    }
  }, []);

  // ── Effects ─────────────────────────────────────────────────────────────────

  useEffect(() => {
    fetchScans();
  }, [fetchScans]);

  useEffect(() => {
    if (activeTab === 'tasks' && !tasksLoaded) fetchTasks();
  }, [activeTab, tasksLoaded, fetchTasks]);

  useEffect(() => {
    if (activeTab === 'tasks' && tasksLoaded) fetchTasks();
  }, [taskPage, activeTab, tasksLoaded, fetchTasks]);

  useEffect(() => {
    if (activeTab === 'mt' && !mtLoaded) fetchMts();
  }, [activeTab, mtLoaded, fetchMts]);

  // ── Derived ─────────────────────────────────────────────────────────────────

  const filteredMts = mtEntries.filter(
    (e) =>
      !mtSearch ||
      e.taskClass.toLowerCase().includes(mtSearch.toLowerCase()) ||
      e.displayName.toLowerCase().includes(mtSearch.toLowerCase()) ||
      (e.description ?? '').toLowerCase().includes(mtSearch.toLowerCase()),
  );
  const mtPagination: Pagination = {
    total: filteredMts.length,
    limit: MT_PAGE_SIZE,
    offset: mtPage * MT_PAGE_SIZE,
    hasMore: (mtPage + 1) * MT_PAGE_SIZE < filteredMts.length,
  };
  const pagedMts = filteredMts.slice(mtPage * MT_PAGE_SIZE, (mtPage + 1) * MT_PAGE_SIZE);

  // ── Render ──────────────────────────────────────────────────────────────────

  const tabs: { id: Tab; label: string; icon: React.ReactNode; badge?: number }[] = [
    { id: 'scans', label: 'Scans', icon: <BarChart3 className="w-4 h-4" /> },
    { id: 'tasks', label: 'Task Executions', icon: <Wrench className="w-4 h-4" /> },
    {
      id: 'mt',
      label: 'Maintenance Tasks',
      icon: <BookOpen className="w-4 h-4" />,
      badge: mtLoaded ? mtEntries.length : undefined,
    },
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <Navbar />
      <main className="pt-24 px-4 pb-12">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
                Admin Dashboard
              </h1>
            </div>
            <button
              onClick={() => {
                if (activeTab === 'scans') fetchScans();
                if (activeTab === 'tasks') fetchTasks();
                if (activeTab === 'mt') fetchMts();
              }}
              className="flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Refresh
            </button>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="flex gap-1">
              {tabs.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setActiveTab(t.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === t.id
                      ? 'border-teal-500 text-teal-600 dark:text-teal-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  {t.icon}
                  {t.label}
                  {t.badge !== undefined && (
                    <span className="px-1.5 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-400">
                      {t.badge}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </div>

          {activeTab === 'scans' && (
            <ScansTab
              scans={scans}
              scansLoading={scansLoading}
              scansError={scansError}
              search={search}
              setSearch={setSearch}
              scanPage={scanPage}
              setScanPage={setScanPage}
              scanPagination={scanPagination}
              fetchScans={fetchScans}
            />
          )}

          {activeTab === 'tasks' && (
            <TasksTab
              tasks={tasks}
              tasksLoading={tasksLoading}
              taskPage={taskPage}
              setTaskPage={setTaskPage}
              taskPagination={taskPagination}
            />
          )}

          {activeTab === 'mt' && (
            <MaintenanceTab
              mtEntries={mtEntries}
              mtLoading={mtLoading}
              mtLoaded={mtLoaded}
              mtSearch={mtSearch}
              setKbSearch={setKbSearch}
              mtPage={mtPage}
              setMtPage={setMtPage}
              filteredMts={filteredMts}
              pagedMts={pagedMts}
              mtPagination={mtPagination}
            />
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AdminScansPage;
